/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
        this.updateOptionVariantIds();
        if (this.variant) this.updateVariantInput();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      // Sync option labels and dropdown options with the matching variant IDs for the current selection state
      this.updateOptionVariantIds();

      const changedSelector = evt.target.closest('.option-selector');
      const changedIndex = Array.from(this.optionSelectors).indexOf(changedSelector);
      if (changedIndex >= 0) {
        this.autoSelectNextOptions(changedIndex);
      }

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const isDisabled = !variantAvailable || this.preSelection;

      // Update the Add to Cart button disabled state and visibility
      this.addBtn.disabled = isDisabled;
      this.addBtn.classList.toggle('variant-unavailable', isDisabled);

      // Update the OOS button visibility (wrapper avoids Klaviyo overriding anchor display/classes)
      this.oosBtnWrapper = this.oosBtnWrapper || this.productForm.querySelector('.product-info__add-button .oos-button-wrapper');
      if (this.oosBtnWrapper) {
        const showOosButton = this.variant && !this.variant.available && !this.preSelection;
        this.oosBtnWrapper.classList.toggle('oos-button-hidden', !showOosButton);
      }

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        el.classList.toggle('is-disabled', !exists);
        if (!exists) {
          el.setAttribute('aria-disabled', 'true');
          el.hidden = true;
        } else {
          el.removeAttribute('aria-disabled');
          el.hidden = false;
        }

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else {
        const label = el.nextElementSibling;

        if (!exists) {
          el.disabled = true;
          el.removeAttribute('aria-label');
          if (label) label.removeAttribute('title');
        } else {
          el.disabled = false;
          if (!available) {
            el.setAttribute('aria-label', `${el.value}, ${unavailableText}`);
            if (label) label.title = unavailableText;
          } else {
            el.removeAttribute('aria-label');
            if (label) label.removeAttribute('title');
          }
        }
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = (selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value).trim();
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;

              for (let i = 0; i < selectorIndex; i += 1) {
                if (this.selectedOptions[i] === null || v.options[i].trim() === this.selectedOptions[i].trim()) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex].trim() === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    autoSelectNextOptions(startIndex) {
      this.optionSelectors.forEach((selector, selectorIndex) => {
        if (selectorIndex <= startIndex) return;
        const isDropdown = selector.dataset.selectorType === 'dropdown';
        if (isDropdown) {
          const customSelect = selector.querySelector('custom-select');
          if (!customSelect) return;
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          const selectedInvalid = selected && selected.classList.contains('is-nonexistent');
          const needsSelection = !selected || selectedInvalid || (selected.dataset.value || '').trim() === '';
          if (!needsSelection) return;
          const candidate = selector.querySelector('.custom-select__option:not(.is-nonexistent)[data-value]:not([data-value=""])');
          if (candidate) {
            customSelect.selectOption(candidate);
          }
        } else {
          const selected = selector.querySelector('input:checked');
          const selectedInvalid = selected && (selected.classList.contains('is-nonexistent') || selected.disabled);
          const needsSelection = !selected || selectedInvalid;
          if (!needsSelection) return;
          const candidate = selector.querySelector('.js-option:not(.is-nonexistent)');
          if (candidate && !candidate.disabled) {
            candidate.checked = true;
            candidate.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    createAndAppendElement(parentElementClass, tagName, textContent) {
      const newElement = document.createElement(tagName);
      const textNode = document.createTextNode(textContent);
      newElement.appendChild(textNode);
      const parentElement = document.getElementById(parentElementClass);

      if (parentElement) {
        parentElement.appendChild(newElement);
      } else {
        console.error(`Parent element with ID '${parentElementClass}' not found.`);
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        priceCurrentEl.dataset.saveText || 'Save';
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');
        const priceDefaultEl = this.price.querySelector('.price__default');
        const savePriceEl = this.price.querySelector('.price__save');
        // Update current price and original price if on sale.
        const onSale = this.variant.compare_at_price > this.variant.price;
        let currentMarkup = this.data.formatted[this.variant.id].price;


        if (onSale) {
          const saveCents = this.variant.compare_at_price - this.variant.price;
          const saveLabel = (window.theme && window.theme.string && window.theme.string.cartSave)
            ? window.theme.string.cartSave
            : ((window.theme && window.theme.strings && window.theme.strings.cartSave) ? window.theme.strings.cartSave : 'Save');
          const saveStr = this.formatMoney(saveCents);
          const data = `<span class="price__save"><span class="price__save-label">${saveLabel}</span> ${saveStr}</span>`;
          savePriceEl?.remove();
          priceDefaultEl.insertAdjacentHTML('afterend', data);
        } else {
          const priceSaveEl = this.price.querySelector('.price__save');
          if (priceSaveEl) priceSaveEl.remove();
        }



        priceCurrentEl.innerHTML = currentMarkup;
        priceCurrentEl.classList.toggle('color-red', onSale);

        priceCurrentEl.innerHTML = theme.priceUtil.stripCurrencySymbols(theme.priceUtil.stripCurrencySymbols(this.data.formatted[this.variant.id].price));
        if (priceWasEl)
          priceWasEl.innerHTML = theme.priceUtil.stripCurrencySymbols(this.data.formatted[this.variant.id].compareAtPrice || '');

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = theme.priceUtil.stripCurrencySymbols(this.data.formatted[this.variant.id].unitPrice);
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        // Update price per serving
        this.updatePricePerServing();

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Find the best matching variant ID for a given option value at selectorIndex,
     * respecting currently selected options in other selectors.
     * Prefers available variants; falls back to any matching variant.
     * @param {number} selectorIndex
     * @param {string} value
     * @returns {number|null}
     */
    findMatchingVariantId(selectorIndex, value) {
      const variants = (this.data && this.data.product && this.data.product.variants) || [];
      let availableMatch = null;
      let anyMatch = null;

      for (let i = 0; i < variants.length; i += 1) {
        const v = variants[i];
        if ((v.options[selectorIndex] || '').trim() !== value) continue;

        let matches = true;
        for (let j = 0; j < this.selectedOptions.length; j += 1) {
          if (j === selectorIndex) continue;
          const sel = this.selectedOptions[j];
          if (sel !== null && (v.options[j] || '').trim() !== sel.trim()) {
            matches = false;
            break;
          }
        }
        if (!matches) continue;

        if (v.available && availableMatch === null) availableMatch = v.id;
        if (anyMatch === null) anyMatch = v.id;
        if (availableMatch !== null) break;
      }

      return availableMatch !== null ? availableMatch : anyMatch;
    }

    /**
     * Update data-variant-id on option labels (listed) and dropdown options to reflect
     * the variant that would be selected if this value were picked, given current selections.
     */
    updateOptionVariantIds() {
      const variants = (this.data && this.data.product && this.data.product.variants) || [];
      if (!variants.length) return;

      // If all variants are on sale, suppress the per-option sale indicator class.
      const allVariantsOnSale = variants.every((v) => v.compare_at_price && v.compare_at_price > v.price);

      this.optionSelectors.forEach((selector, selectorIndex) => {
        const s = /** @type {HTMLElement} */ (selector);
        const isDropdown = s.dataset.selectorType === 'dropdown';

        if (isDropdown) {
          const options = s.querySelectorAll('.custom-select__option');
          options.forEach((opt) => {
            const optEl = /** @type {HTMLElement} */ (opt);
            const value = (optEl.dataset.value || '').trim();
            if (value === '') {
              optEl.removeAttribute('data-variant-id');
              optEl.classList.toggle('has-compare-price', false);
              return;
            }
            const id = this.findMatchingVariantId(selectorIndex, value);
            if (id) {
              optEl.setAttribute('data-variant-id', String(id));
            } else {
              optEl.removeAttribute('data-variant-id');
            }
            // Toggle compare-at-price class based on the matched variant for this value
            let hasCompare = false;
            if (id) {
              const v = variants.find((vv) => vv.id === id);
              const isOnSale = !!(v && v.compare_at_price && v.compare_at_price > v.price);
              // Suppress indicator when all variants are on sale
              hasCompare = !allVariantsOnSale && isOnSale;
            }
            optEl.classList.toggle('has-compare-price', hasCompare);
          });
        } else {
          const inputs = s.querySelectorAll('input.js-option');
          inputs.forEach((input) => {
            const inp = /** @type {HTMLInputElement} */ (input);
            const value = (inp.value || '').trim();
            const label = /** @type {HTMLElement|null} */ (inp.nextElementSibling);
            if (!label || value === '' || !label.classList.contains('opt-label')) return;

            const id = this.findMatchingVariantId(selectorIndex, value);
            if (id) {
              label.setAttribute('data-variant-id', String(id));
            } else {
              label.removeAttribute('data-variant-id');
            }

            // Toggle compare-at-price class based on the matched variant for this value
            let hasCompare = false;
            if (id) {
              const v = variants.find((vv) => vv.id === id);
              const isOnSale = !!(v && v.compare_at_price && v.compare_at_price > v.price);
              // Suppress indicator when all variants are on sale
              hasCompare = !allVariantsOnSale && isOnSale;
            }
            label.classList.toggle('has-compare-price', hasCompare);
          });
        }
      });
    }

    /**
     * Updates the price per serving display.
     */
    updatePricePerServing() {
      if (!this.variant) return;

      // Find the price per serving container
      const pricePerServingContainer = this.section.querySelector('.js-price-per-serving');
      if (!pricePerServingContainer) return;

      // Find the price element
      const priceElement = pricePerServingContainer.querySelector('.price-per-serving__price');
      if (!priceElement) return;

      // Get the current variant ID
      const variantId = this.variant.id.toString();

      // Find the variant metafield data element
      const metafieldElement = this.section.querySelector(`.js-variant-metafields span[data-variant-id="${variantId}"]`);

      // Get package size from the data attribute
      let packageSize = null;
      if (metafieldElement && metafieldElement.dataset.packageSize) {
        packageSize = parseFloat(metafieldElement.dataset.packageSize);
      }

      if (packageSize && packageSize > 0) {
        // Calculate price per serving
        const pricePerServing = this.variant.price / packageSize;

        // Use the same price format as the main price
        priceElement.innerHTML = this.formatMoney(pricePerServing);

        // Show the container
        pricePerServingContainer.hidden = false;
      } else {
        // Hide the container if no package size
        pricePerServingContainer.hidden = true;
      }
    }

    /**
     * Formats a price as money with the current currency.
     * @param {Number} cents - The price in cents to format.
     * @returns {String} - The formatted price with currency.
     */
    formatMoney(cents) {
      // If we have formatted data for the current variant, use that format
      if (this.variant && this.data.formatted && this.data.formatted[this.variant.id]) {
        // Get the current price and unit price to extract the format
        const currentPrice = this.data.formatted[this.variant.id].price;

        // If Shopify's formatMoney is available, use it
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          const formatted = Shopify.formatMoney(cents, Shopify.money_format);
          return theme.priceUtil.stripCurrencySymbols(formatted);
        }

        // Otherwise, try to match the format of the current price
        const price = (cents / 100).toFixed(2);
        let formatted = '';
        if (currentPrice.includes('€')) {
          formatted = currentPrice.includes('€ ') ? `€ ${price}` : `€${price}`;
        } else if (currentPrice.includes('£')) {
          formatted = currentPrice.includes('£ ') ? `£ ${price}` : `£${price}`;
        } else {
          // Default to $ if no other currency detected
          formatted = `$${price}`;
        }
        return theme.priceUtil.stripCurrencySymbols(formatted);
      }

      // Fallback to Shopify's formatMoney
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        const formatted = Shopify.formatMoney(cents);
        return theme.priceUtil.stripCurrencySymbols(formatted);
      }

      // Basic fallback
      const formatted = `$${(cents / 100).toFixed(2)}`;
      return theme.priceUtil.stripCurrencySymbols(formatted);
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          // Read the actual selected option value from the custom-select list (data-value).
          const selectedOption = selector.querySelector('.custom-select__option[aria-selected="true"]') || selector.querySelector('.custom-select__listbox')?.firstElementChild;
          const selectedValue = selectedOption?.dataset?.value?.trim() || '';
          selectedOptions.push(selectedValue === '' ? null : selectedValue);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find((v) =>
        v.options.every((val, index) => {
          const selected = selectedOptions[index];
          return selected !== null && val.trim() === selected.trim();
        })
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
